'use client'

import { ButtonSpinner, type ButtonSpinnerTone } from '@tebuto/layout/Spinner'
import { cn } from '@tebuto/lib/utils'

export type ButtonType = 'button' | 'submit' | 'reset'
export type ButtonLayout = 'default' | 'ghost' | 'outline'

type IButton = {
    href?: string
    type?: ButtonType
    className?: string
    disabled?: boolean
    isLoading?: boolean
    layout?: ButtonLayout
} & React.HTMLProps<HTMLButtonElement>

function getCheckoutSpinnerTone(layout: ButtonLayout | undefined): ButtonSpinnerTone {
    if (layout === 'outline' || layout === 'ghost') {
        return 'brand'
    }
    return 'on-inverse'
}

export function CheckoutButton({ children, onClick, className, disabled, isLoading, layout, type, ...props }: IButton) {
    const defaultStyling =
        'relative w-full py-2 rounded font-medium text-sm shadow-sm flex justify-center items-center px-4 whitespace-nowrap transition-colors duration-200 ease-in-out focus:outline-none group'

    function getLayoutStyling() {
        switch (layout) {
            case 'ghost':
                return 'bg-transparent shadow-none p-0 m-0 hover:bg-transparent widget-text-secondary'
            case 'outline':
                return 'widget-border border widget-text-secondary widget-bg-field'
            default:
                return 'widget-bg-primary-600 widget-text-on-primary'
        }
    }

    const styling = cn(
        defaultStyling,
        disabled ? 'cursor-not-allowed opacity-50' : '',
        className ?? '',
        getLayoutStyling()
    )

    return (
        <button
            type={type}
            className={styling}
            onMouseDown={type === 'submit' ? undefined : (event) => event.preventDefault()}
            disabled={disabled || isLoading}
            {...props}
            onClick={onClick}
        >
            <div className={cn(isLoading ? 'absolute inset-0 flex items-center justify-center' : 'hidden')}>
                <ButtonSpinner tone={getCheckoutSpinnerTone(layout)} />
            </div>
            <div className={cn(isLoading ? 'invisible' : '')}>{children}</div>
        </button>
    )
}
